import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CHARTE_VERSION } from "@/lib/charter";

const Charter = () => {
  return (
    <>
      <SEO
        title="Charte du Collectif Ibbü | Conseillers iAdvize Ibbu"
        description="Charte du Collectif Ibbü : règles, objectifs et engagements pour l'action collective des conseillers iAdvize/Ibbu."
        canonical="https://collectif-ibbu.org/charte"
        keywords="Ibbü, Ibbu, iAdvize, charte, collectif, conseillers, règlement"
      />
      <main className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>

          <Breadcrumbs items={[{ label: "Charte du Collectif" }]} />

          {/* Page Header with H1 */}
          <div className="text-center space-y-4 mb-8">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Charte du Collectif des conseillers iAdvize/Ibbü
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Règles, objectifs et engagements du collectif
            </p>
          </div>

          {/* Main Card */}
          <Card className="border-0 shadow-xl">
            <CardContent className="prose prose-lg max-w-none space-y-6">
              <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                <strong>Collectif indépendant d'anciens et actuels conseillers de la plateforme iAdvize/Ibbü, sans lien, partenariat ni affiliation avec la société iAdvize.</strong> Les marques « iAdvize » et « Ibbü » sont citées à seule fin d'identifier l'entreprise concernée par la présente démarche.
              </blockquote>

              <p className="leading-relaxed">
                En rejoignant le Collectif, chaque membre s'engage à lire et à respecter les principes et règles énoncés ci-dessous.
              </p>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">1. Objet du Collectif</h2>
                <p className="leading-relaxed mb-4">
                  Le Collectif a pour unique objectif de regrouper d'anciens et actuels conseillers de la plateforme iAdvize/Ibbü afin de :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Partager des informations et des témoignages sur leurs conditions de travail.</li>
                  <li>S'informer mutuellement sur nos droits.</li>
                  <li>Organiser une démarche juridique groupée pour faire valoir nos droits devant les juridictions compétentes.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
                  2. Rôle des Initiateurs et Nature du Collectif
                </h2>
                <p className="leading-relaxed">
                  Le collectif est une initiative de pairs, créée et animée par d'anciens conseillers. Les initiateurs ne sont ni avocats, ni juristes, ni professionnels du droit. Le collectif n'est pas un syndicat ni une association déclarée. Il agit comme un groupement informel, indépendant de la société iAdvize/Ibbü.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
                  3. Règles de Comportement
                </h2>
                <p className="leading-relaxed mb-4">Chaque membre s'engage à :</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Respecter la confidentialité</strong>, dans les conditions détaillées à l'article 3 ter.
                  </li>
                  <li>
                    <strong>Adopter une communication respectueuse :</strong> Les échanges doivent être courtois et constructifs. Tout propos injurieux, discriminatoire ou relevant du harcèlement est proscrit.
                  </li>
                  <li>
                    <strong>Interdiction du harcèlement et de l'intimidation :</strong> Tout harcèlement, intimidation, menace ou comportement agressif envers un autre membre du collectif est strictement interdit et ne sera pas toléré. Cela inclut les attaques personnelles, le harcèlement moral, la pression répétée ou tout comportement visant à faire taire, marginaliser ou menacer un membre. Tout manquement à cette règle constitue un manquement grave susceptible d'entraîner la suspension puis l'exclusion du Collectif dans les conditions prévues à l'article 4.
                  </li>
                  <li>
                    <strong>Ne pas commettre d'actes illégaux :</strong> Il est formellement interdit de tenir des propos diffamatoires envers iAdvize/Ibbü, ses dirigeants ou toute autre personne. Les membres doivent s'en tenir à des faits et des expériences personnelles, sans porter d'accusations publiques non vérifiées. Chaque membre est personnellement responsable de ses propres propos.
                  </li>
                  <li>
                    <strong>Ne pas démarcher les autres membres :</strong> Tout démarchage auprès des membres du collectif (sollicitations commerciales, offres de services, propositions de recrutement, promotion de plateformes concurrentes, prospection ou toute autre forme de sollicitation non sollicitée) est strictement interdit. Les coordonnées et l'appartenance au collectif ne peuvent en aucun cas être utilisées à des fins personnelles, commerciales ou promotionnelles. Tout manquement à cette règle constitue un manquement grave susceptible d'entraîner la suspension puis l'exclusion du Collectif dans les conditions prévues à l'article 4.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
                  3 bis. Engagement de bonne foi et déclaration sur l'honneur
                </h2>
                <p className="leading-relaxed mb-4">
                  L'adhésion au Collectif s'effectue de bonne foi (art. 1104 du Code civil). En adhérant, chaque membre déclare sur l'honneur :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>être ou avoir été effectivement conseiller de la plateforme iAdvize/Ibbü ;</li>
                  <li>rejoindre le Collectif afin de défendre ses propres droits et intérêts et/ou de contribuer de bonne foi à l'objet du Collectif tel que défini à l'article 1 ;</li>
                  <li>n'agir ni pour le compte, ni dans l'intérêt, ni sur instruction de la société iAdvize/Ibbü, de ses dirigeants, préposés ou conseils ;</li>
                  <li>n'entretenir avec ces derniers aucun mandat, mission, instruction, intérêt ou relation de nature à le placer en situation de conflit avec l'objet du Collectif.</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  Le membre s'interdit de participer, directement ou indirectement, pour le compte d'iAdvize/Ibbü, de ses dirigeants, préposés, conseils ou de tout tiers agissant pour eux, à la collecte d'informations relatives au Collectif.
                </p>
                <p className="leading-relaxed mt-4">
                  Tout comportement consistant à transmettre des informations à iAdvize/Ibbü ou à ses représentants, ou à nuire de quelque manière que ce soit à la démarche collective, constitue un manquement grave au sens de l'article 4, et engage la responsabilité personnelle de son auteur sur le fondement de l'article 1240 du Code civil.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
                  3 ter. Confidentialité et protection des échanges
                </h2>

                <p className="leading-relaxed mb-4">
                  <strong>Informations confidentielles.</strong> Sont notamment confidentiels : l'identité et les coordonnées des membres, leurs témoignages, pièces et documents, les projets d'argumentation et éléments de stratégie contentieuse, les échanges internes (groupe de messagerie, e-mails, espace membre), les documents de travail, ainsi que toute information signalée comme confidentielle ou dont le caractère non public ressort des circonstances.
                </p>

                <p className="leading-relaxed mb-4">
                  <strong>Obligations.</strong> Chaque membre s'interdit de divulguer, transmettre, copier ou réutiliser ces informations en dehors du Collectif, sans l'accord explicite des personnes concernées et, s'agissant des informations relatives à l'organisation, au fonctionnement ou à la stratégie du Collectif, sans l'accord des initiateurs. Il est notamment interdit, sauf dans les cas prévus au présent article, de réaliser, de conserver hors des espaces du Collectif, de diffuser ou de transmettre des captures d'écran, enregistrements ou copies des échanges et documents internes, en particulier à iAdvize/Ibbü ou à ses représentants.
                </p>

                <p className="leading-relaxed mb-4">
                  <strong>Durée.</strong> Cette obligation demeure applicable même après le départ ou l'exclusion du membre, aussi longtemps que les informations concernées n'ont pas perdu leur caractère non public ou stratégique par une divulgation licite émanant de la personne concernée ou effectuée avec son accord.
                </p>

                <p className="leading-relaxed mb-4">
                  <strong>Exceptions.</strong> Ne constitue pas une violation de la présente obligation la communication strictement nécessaire d'éléments confidentiels à l'avocat du membre, à une juridiction, à une autorité légalement compétente, pour l'exercice ou la défense de droits en justice, ou pour répondre à une obligation légale, la divulgation étant limitée au strict nécessaire.
                </p>

                <p className="leading-relaxed mb-4">
                  <strong>Incident.</strong> Le membre informe sans délai les initiateurs de toute compromission, diffusion non autorisée, sollicitation émanant d'iAdvize/Ibbü ou de ses représentants, ou suspicion d'infiltration.
                </p>

                <p className="leading-relaxed mb-4">
                  <strong>Départ.</strong> En cas de départ ou d'exclusion, le membre cesse immédiatement tout accès aux espaces du Collectif et supprime les copies des échanges et documents qui ne sont pas nécessaires à l'exercice licite de ses propres droits.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">4. Suspension et exclusion du Collectif</h2>
                <p className="leading-relaxed mb-4">
                  En cas d'indices sérieux de manquement grave à la présente charte (notamment fausse déclaration lors de l'inscription, rupture de la confidentialité, démarchage, transmission d'informations à iAdvize/Ibbü ou à ses représentants, harcèlement, ou propos illicites), l'accès du membre aux espaces du Collectif peut être suspendu à titre conservatoire avec effet immédiat.
                </p>
                <p className="leading-relaxed">
                  L'exclusion définitive peut être prononcée après que le membre a été invité à présenter ses observations dans un délai de 48 heures, sauf urgence caractérisée ou impossibilité. L'exclusion entraîne la cessation immédiate de tout accès aux espaces du Collectif et de ses espaces de discussion.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">5. Limitation de Responsabilité</h2>
                <p className="leading-relaxed mb-4">En rejoignant le collectif, vous reconnaissez et acceptez que :</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Les initiateurs du Collectif agissent bénévolement. Ils ne garantissent ni le succès ni l'issue de la démarche juridique entreprise.
                  </li>
                  <li>
                    Le collectif n'offre aucun conseil juridique. Les informations partagées ne se substituent en aucun cas à la consultation d'un avocat.
                  </li>
                  <li>
                    Sous réserve de leurs obligations légales impératives, les initiateurs ne répondent pas des propos, actes ou manquements personnels commis par les membres en violation de la loi ou de la présente charte.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">6. Protection des données personnelles (RGPD)</h2>
                <p className="leading-relaxed mb-4">
                  Le traitement des données personnelles des membres (collecte, finalités, base légale, durée de conservation, destinataires, sécurité et droits) est régi par la politique de confidentialité du Collectif, accessible à l'adresse suivante :{' '}
                  <a
                    href="https://collectif-ibbu.org/confidentialite"
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://collectif-ibbu.org/confidentialite
                  </a>
                </p>
                <p className="leading-relaxed">
                  En adhérant, chaque membre reconnaît en avoir pris connaissance. L'accès aux données nominatives et aux éléments stratégiques de la démarche est restreint aux seuls initiateurs habilités et n'est pas accessible à l'ensemble des membres.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">7. Entrée en vigueur et modification de la charte</h2>
                <p className="leading-relaxed">
                  La présente charte est applicable dans sa version en vigueur au jour de l'adhésion du membre. Toute modification substantielle fait l'objet d'une information préalable des membres et d'une nouvelle acceptation avant de leur être applicable.
                </p>
              </section>

              <div className="pt-8 border-t border-border mt-8">
                <p className="text-sm text-muted-foreground text-center">
                  Version {CHARTE_VERSION}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

export default Charter;
